import { Link, useForm } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';
import FormInput from '@/Components/Form/FormInput';
import FormTextarea from '@/Components/Form/FormTextarea';
import FormSelect from '@/Components/Form/FormSelect';
import FormSection from '@/Components/Form/FormSection';

export default function QueriesEdit({ query, clients, destinations, users, statuses, types, priorities, sources }) {
    const { data, setData, put, processing, errors } = useForm({
        title: query.title || '',
        client_id: query.client_id || '',
        guest_name: query.guest_name || '',
        contact_email: query.contact_email || '',
        contact_phone: query.contact_phone || '',
        travel_date: query.travel_date || '',
        return_date: query.return_date || '',
        nights: query.nights || 0,
        adults: query.adults || 2,
        children: query.children || 0,
        infants: query.infants || 0,
        destinations: query.destinations || [],
        query_type: query.query_type || 'package',
        status: query.status || 'pending',
        priority: query.priority || 'warm',
        source: query.source || '',
        assigned_to: query.assigned_to || '',
        budget: query.budget || '',
        quoted_amount: query.quoted_amount || '',
        requirements: query.requirements || '',
        internal_notes: query.internal_notes || '',
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        put(`/queries/${query.id}`);
    };

    const typeOptions = Object.entries(types).map(([key, label]) => ({ id: key, name: label }));
    const priorityOptions = Object.entries(priorities).map(([key, label]) => ({ id: key, name: label }));
    const sourceOptions = Object.entries(sources).map(([key, label]) => ({ id: key, name: label }));
    const statusOptions = Object.entries(statuses).map(([key, label]) => ({ id: key, name: label }));

    return (
        <AppLayout title={`Edit ${query.query_number}`}>
            <PageHeader
                title="Edit Query"
                subtitle={query.query_number}
                breadcrumbs={[
                    { label: 'CRM' },
                    { label: 'Queries', href: '/queries' },
                    { label: 'Edit' },
                ]}
            />

            <form onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-lg-8">
                        <FormSection title="Query Details">
                            <FormInput
                                className="col-12"
                                label="Query Title"
                                name="title"
                                value={data.title}
                                onChange={(v) => setData('title', v)}
                                error={errors.title}
                                required
                            />
                            <FormSelect
                                className="col-md-4"
                                label="Query Type"
                                name="query_type"
                                value={data.query_type}
                                onChange={(v) => setData('query_type', v)}
                                options={typeOptions}
                                searchable
                            />
                            <FormSelect
                                className="col-md-4"
                                label="Priority"
                                name="priority"
                                value={data.priority}
                                onChange={(v) => setData('priority', v)}
                                options={priorityOptions}
                                searchable
                            />
                            <FormSelect
                                className="col-md-4"
                                label="Lead Source"
                                name="source"
                                value={data.source}
                                onChange={(v) => setData('source', v)}
                                options={sourceOptions}
                                searchable
                            />
                        </FormSection>

                        <FormSection title="Client / Guest">
                            <FormSelect
                                className="col-md-6"
                                label="Client"
                                name="client_id"
                                value={data.client_id}
                                onChange={(v) => setData('client_id', v)}
                                options={clients}
                                searchable
                                placeholder="Select client..."
                            />
                            <FormInput
                                className="col-md-6"
                                label="Guest Name"
                                name="guest_name"
                                value={data.guest_name}
                                onChange={(v) => setData('guest_name', v)}
                            />
                            <FormInput
                                className="col-md-6"
                                label="Email"
                                name="contact_email"
                                type="email"
                                value={data.contact_email}
                                onChange={(v) => setData('contact_email', v)}
                            />
                            <FormInput
                                className="col-md-6"
                                label="Phone"
                                name="contact_phone"
                                value={data.contact_phone}
                                onChange={(v) => setData('contact_phone', v)}
                            />
                        </FormSection>

                        <FormSection title="Travel Details">
                            <FormInput
                                className="col-md-4"
                                label="Travel Date"
                                name="travel_date"
                                type="date"
                                value={data.travel_date}
                                onChange={(v) => setData('travel_date', v)}
                            />
                            <FormInput
                                className="col-md-4"
                                label="Return Date"
                                name="return_date"
                                type="date"
                                value={data.return_date}
                                onChange={(v) => setData('return_date', v)}
                            />
                            <FormInput
                                className="col-md-4"
                                label="Nights"
                                name="nights"
                                type="number"
                                value={data.nights}
                                onChange={(v) => setData('nights', parseInt(v) || 0)}
                            />
                            <FormInput
                                className="col-md-4"
                                label="Adults"
                                name="adults"
                                type="number"
                                value={data.adults}
                                onChange={(v) => setData('adults', parseInt(v) || 1)}
                                min="1"
                            />
                            <FormInput
                                className="col-md-4"
                                label="Children"
                                name="children"
                                type="number"
                                value={data.children}
                                onChange={(v) => setData('children', parseInt(v) || 0)}
                            />
                            <FormInput
                                className="col-md-4"
                                label="Infants"
                                name="infants"
                                type="number"
                                value={data.infants}
                                onChange={(v) => setData('infants', parseInt(v) || 0)}
                            />
                            <FormTextarea
                                className="col-12"
                                label="Requirements"
                                name="requirements"
                                value={data.requirements}
                                onChange={(v) => setData('requirements', v)}
                                rows={3}
                            />
                        </FormSection>

                        <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-primary" disabled={processing}>
                                {processing ? 'Saving...' : 'Save Changes'}
                            </button>
                            <Link href="/queries" className="btn btn-outline-secondary">Cancel</Link>
                        </div>
                    </div>

                    <div className="col-lg-4">
                        <FormSection title="Status & Assignment">
                            <FormSelect
                                className="col-12"
                                label="Status"
                                name="status"
                                value={data.status}
                                onChange={(v) => setData('status', v)}
                                options={statusOptions}
                                searchable
                            />
                            <FormSelect
                                className="col-12"
                                label="Assigned To"
                                name="assigned_to"
                                value={data.assigned_to}
                                onChange={(v) => setData('assigned_to', v)}
                                options={users}
                                searchable
                                placeholder="Select User"
                            />
                        </FormSection>

                        <FormSection title="Budget & Pricing">
                            <FormInput
                                className="col-12"
                                label="Client Budget (INR)"
                                name="budget"
                                type="number"
                                value={data.budget}
                                onChange={(v) => setData('budget', v)}
                            />
                            <FormInput
                                className="col-12"
                                label="Quoted Amount (INR)"
                                name="quoted_amount"
                                type="number"
                                value={data.quoted_amount}
                                onChange={(v) => setData('quoted_amount', v)}
                            />
                        </FormSection>

                        <FormSection title="Internal Notes">
                            <FormTextarea
                                className="col-12"
                                name="internal_notes"
                                value={data.internal_notes}
                                onChange={(v) => setData('internal_notes', v)}
                                rows={4}
                            />
                        </FormSection>

                        <div className="card">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Info</h5>
                            </div>
                            <div className="card-body">
                                <div className="mb-2">
                                    <small className="text-muted d-block">Query Number</small>
                                    <span className="fw-semibold">{query.query_number}</span>
                                </div>
                                <div className="mb-2">
                                    <small className="text-muted d-block">Created</small>
                                    <span>{new Date(query.created_at).toLocaleString()}</span>
                                </div>
                                <div>
                                    <small className="text-muted d-block">Last Updated</small>
                                    <span>{new Date(query.updated_at).toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </AppLayout>
    );
}
